import { HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { Account } from "src/app/pages/account/models/account";
import { TradeSummaryPresentation } from "src/app/pages/allocation/models/trade-summary-presentation";
import { GroupDetail } from "src/app/pages/position/models/group-detail";
import { Position } from "src/app/pages/position/models/position";
import { PositionSearch } from "src/app/pages/position/models/position-search";
import { SymbolDetail } from "src/app/pages/position/models/symbol-detail";
import { SymbolPosition } from "src/app/pages/position/models/symbol-position";
import { APP_CONFIG } from "../app.config";
import { AppConfig } from "../models/app.config";
import { ApiService } from "./api.service";

@Injectable({
    providedIn: "root"
})

export class PositionService {
    private readonly positionsUrl: string = "Position/";
    config: AppConfig;

    public accountPkChangedSubject = new BehaviorSubject<number>(0);
    public accountPkChanged$ = this.accountPkChangedSubject.asObservable();

    public searchExecutedSubject = new BehaviorSubject<PositionSearch>({ riaId: 0, symbol: "", group: "", side: "" });
    public searchExecuted$ = this.searchExecutedSubject.asObservable();

    public positionRefreshSubject = new BehaviorSubject<boolean>(false);
    public positionRefresh$ = this.positionRefreshSubject.asObservable();

    constructor(private apiService: ApiService, @Inject(APP_CONFIG) config: AppConfig) {
        this.config = config;
    }

    public getAllDVPAccountsAndDetail(riaId: number): Observable<Account[]> {
        const params = new HttpParams()
            .set("riaID", riaId);
        return this.apiService.get<Account[]>(`${this.positionsUrl}GetAllDVPAccountsAndDetail`, params);
    }

    public getAllAccounts(riaId: number): Observable<Account[]> {
        const params = new HttpParams()
            .set("riaID", riaId);
        return this.apiService.get<Account[]>(`${this.positionsUrl}GetAllAccounts`, params);
    }

    public getDVPAccountPosition(riaId: number, accountPk: number): Observable<Position[]> {
        const params = new HttpParams()
            .set("riaID", riaId)
            .set("accountPk", accountPk);
        return this.apiService.get<Position[]>(`${this.positionsUrl}GetDVPAccountPosition`, params);
    }

    public getAccountPositions(riaId: number, accountPk: number): Observable<Position[]> {
        const params = new HttpParams()
            .set("riaID", riaId)
            .set("accountPk", accountPk);
        return this.apiService.get<Position[]>(`${this.positionsUrl}GetAccountPosition`, params);
    }

    public getGroupPositionSummary(positionSearch: PositionSearch): Observable<Position[]> {
        const params = new HttpParams()
            .set("riaID", positionSearch.riaId)
            .set("symbol", positionSearch.symbol ?? "")
            .set("riaGroupId", positionSearch.group)
            .set("side", positionSearch.side);
        if (positionSearch.side !== 'N') {
            return this.apiService.get<Position[]>(`${this.positionsUrl}GetGroupPositionSummary`, params);
        }
        else {
            return this.apiService.get<Position[]>(`${this.positionsUrl}GetAccountsNPosition`, params);
        }
    }

    public getSymbolSummary(symbolDetail: SymbolDetail): Observable<Symbol> {
        const params = new HttpParams()
            .set("riaID", symbolDetail.riaId)
            .set("symbol", symbolDetail.symbol ?? "")
            .set("riaGroupId", symbolDetail.group);
        return this.apiService.get<Symbol>(`${this.positionsUrl}GetSymbolSummary`, params);
    }

    public getSymbolPosition(symbolDetail: SymbolPosition): Observable<TradeSummaryPresentation[]> {
        const params = new HttpParams()
            .set("riaID", symbolDetail.riaId)
            .set("symbol", symbolDetail.symbol)
            .set("longShort", "")
            .set("riaGroupId", symbolDetail.group);
        return this.apiService.get<TradeSummaryPresentation[]>(`${this.positionsUrl}GetSymbolPosition`, params);
    }

    public getAllGroups(riaID: number): Observable<GroupDetail[]> {
        const params = new HttpParams()
            .set("riaID", riaID);
        return this.apiService.get<GroupDetail[]>(`${this.positionsUrl}GetAllGroups`, params);
    }

    public triggerPositionRefresh(): void {
        this.positionRefreshSubject.next(true);
    }
}